// Les cartes d'Arceau Game (D-161, D-162) : lisibles à 5 m, un objectif énorme, un verbe, un picto, un effet.
// Trois sortes : Défi (à faire sur le terrain), Action (effet immédiat), Joker (à garder). Les défis viennent des
// exercices du coach (« En faire une carte », D-063) ou des 3 paquets de départ écrits par Arceau (U9–U11, U13–U15,
// U17 et adultes), à relire par un entraîneur avant l'ouverture. Aucune IA : des modèles écrits une fois, que le coach
// ajuste. Logique pure ; tout ce qui vient de la base est relu sans confiance (readCard, readDeck).
// Lot 2 (D-167) : une carte Défi peut avoir plusieurs lignes (« 5 tirs à 3 points, puis 5 flotteurs ») ; Le Sablier lit le
// même paquet : un défi réussi peut rendre du temps (+20 s ou +30 s, `sec`), ses actions sont « Sablier retourné » (+30 s)
// et « Coup de vent » (−15 s à l'équipe qui a le plus de sable). Un paquet passe d'un jeu à l'autre (toSablierDeck,
// toParcoursCard) : les cartes gardées dans « Mes paquets » servent aux deux.

import type { Category } from './session';

export type CardKind = 'defi' | 'action' | 'joker';
export type Fam = 'tir' | 'dribble' | 'passe' | 'physique';
export const FAMS: Fam[] = ['tir', 'dribble', 'passe', 'physique'];
export const FAM_LABEL: Record<Fam, string> = { tir: 'Tir', dribble: 'Dribble', passe: 'Passe', physique: 'Physique' };
export const FAM_HEX: Record<Fam, string> = { tir: '#E8590C', dribble: '#2F6DD8', passe: '#15803D', physique: '#7C3AED' };

/** L'effet d'un défi réussi. */
export type Win = 'replay' | 'plus1' | 'plus2';
export const WINS: Win[] = ['replay', 'plus1', 'plus2'];
export const WIN_LABEL: Record<Win, string> = { replay: 'Relance', plus1: '+1 case', plus2: '+2 cases' };
/** Les actions (effet immédiat) et les jokers (à garder) du lot 1. */
export type ActionFx = 'plus2' | 'replay' | 'leaderBack' | 'flip' | 'wind';
export type JokerFx = 'change' | 'cancel';
/** Les actions du Parcours ; celles du Sablier (D-167). */
export const ACTION_FX: ActionFx[] = ['plus2', 'replay', 'leaderBack'];
export const SABLIER_FX: ActionFx[] = ['flip', 'wind'];
const ALL_FX: ActionFx[] = [...ACTION_FX, ...SABLIER_FX];
export const JOKER_FX: JokerFx[] = ['change', 'cancel'];
export const ACTION_LABEL: Record<ActionFx, string> = {
  plus2: '+2 cases', replay: 'Rejoue', leaderBack: 'Le pion de tête recule de 1', flip: 'Sablier retourné', wind: 'Coup de vent',
};
/** Le Sablier : ce qu'un défi réussi rend (secondes), ce que font ses actions. */
export type SecGain = 0 | 20 | 30;
export const SEC_GAINS: SecGain[] = [0, 20, 30];
export const FLIP_S = 30;
export const WIND_S = 15;
export const JOKER_LABEL: Record<JokerFx, string> = { change: 'Change de défi', cancel: 'Annule un raté' };

export type Side = 'droit' | 'gauche' | 'deux';
export type Hand = 'droite' | 'gauche' | 'any';
export const SIDE_LABEL: Record<Side, string> = { droit: 'côté droit', gauche: 'côté gauche', deux: 'des deux côtés' };
export const HAND_LABEL: Record<Hand, string> = { droite: 'main droite', gauche: 'main gauche', any: '' };

/** Une ligne de plus d'un défi : « puis 5 flotteurs ». */
export interface CardLine { n: number | null; verb: string }
export const MORE_MAX = 2;

export interface Card {
  kind: CardKind;
  /** Défi : la famille, l'objectif (un nombre, un verbe), le côté, la main, des précisions libres. */
  fam?: Fam;
  n?: number | null;
  verb: string;
  side?: Side | null;
  hand?: Hand | null;
  tags?: string[];
  /** Les lignes suivantes du défi (« puis 5 flotteurs »), deux au plus. */
  more?: CardLine[];
  /** « Réussi si » ; vide : écrit tout seul (autoOk). */
  ok?: string;
  win?: Win;
  /** Le Sablier : ce que le défi réussi rend, en secondes (0 : le sable s'arrête, c'est tout). */
  sec?: SecGain;
  /** Action ou joker : son effet. */
  fx?: ActionFx | JokerFx;
  /** La carte faite depuis un exercice (« En faire une carte »). */
  exercise_id?: string | null;
}

export const DECK_MAX = 40;
export const VERB_MAX = 40;
export const OK_MAX = 140;

const str = (v: unknown, max: number) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '');
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Une carte relue sans confiance ; null si elle ne tient pas debout. */
export function readCard(v: unknown): Card | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const kind = o.kind === 'action' || o.kind === 'joker' ? o.kind : o.kind === 'defi' ? 'defi' : null;
  if (!kind) return null;
  if (kind === 'action') {
    const fx = ALL_FX.includes(o.fx as ActionFx) ? o.fx as ActionFx : null;
    return fx ? { kind, fx, verb: ACTION_LABEL[fx] } : null;
  }
  if (kind === 'joker') {
    const fx = JOKER_FX.includes(o.fx as JokerFx) ? o.fx as JokerFx : null;
    return fx ? { kind, fx, verb: JOKER_LABEL[fx] } : null;
  }
  const verb = str(o.verb, VERB_MAX);
  if (!verb) return null;
  const n = typeof o.n === 'number' && Number.isFinite(o.n) ? Math.max(1, Math.min(99, Math.round(o.n))) : null;
  const card: Card = {
    kind, verb, n,
    fam: FAMS.includes(o.fam as Fam) ? o.fam as Fam : 'tir',
    side: o.side === 'droit' || o.side === 'gauche' || o.side === 'deux' ? o.side : null,
    hand: o.hand === 'droite' || o.hand === 'gauche' || o.hand === 'any' ? o.hand : null,
    tags: Array.isArray(o.tags) ? o.tags.map(t => str(t, 24)).filter(Boolean).slice(0, 3) : [],
    ok: str(o.ok, OK_MAX),
    win: WINS.includes(o.win as Win) ? o.win as Win : 'plus1',
  };
  const more = readLines(o.more);
  if (more.length) card.more = more;
  if (SEC_GAINS.includes(o.sec as SecGain)) card.sec = o.sec as SecGain;
  if (typeof o.exercise_id === 'string' && UUID.test(o.exercise_id)) card.exercise_id = o.exercise_id;
  return card;
}
/** Les lignes de plus d'un défi, relues : un verbe chacune, deux au plus. */
function readLines(v: unknown): CardLine[] {
  if (!Array.isArray(v)) return [];
  const out: CardLine[] = [];
  for (const x of v) {
    if (!x || typeof x !== 'object') continue;
    const l = x as Record<string, unknown>;
    const verb = str(l.verb, VERB_MAX);
    if (!verb) continue;
    out.push({ n: typeof l.n === 'number' && Number.isFinite(l.n) ? Math.max(1, Math.min(99, Math.round(l.n))) : null, verb });
    if (out.length >= MORE_MAX) break;
  }
  return out;
}

/** Un paquet relu sans confiance : 40 cartes au plus. */
export const readDeck = (v: unknown): Card[] => (Array.isArray(v) ? v.map(readCard).filter((c): c is Card => !!c).slice(0, DECK_MAX) : []);

/** Les précisions de la carte : le côté, la main, puis les mots libres (« sur place »). */
export function cardTags(c: Card): string[] {
  const out: string[] = [];
  if (c.side) out.push(SIDE_LABEL[c.side]);
  if (c.hand && c.hand !== 'any') out.push(HAND_LABEL[c.hand]);
  for (const t of c.tags ?? []) if (!out.includes(t)) out.push(t);
  return out;
}

/** « Réussi si », écrit tout seul à partir de l'objectif, quand le coach n'a rien écrit. */
export function autoOk(c: Pick<Card, 'fam' | 'n' | 'verb' | 'side'>): string {
  const n = c.n ?? null;
  switch (c.fam) {
    case 'tir': return n ? `${n} paniers marqués${c.side === 'deux' ? ', on alterne les côtés' : ''}, chacun son tour.` : 'Le panier marqué.';
    case 'dribble': return n ? `${n} sans perdre le ballon.` : 'Sans perdre le ballon.';
    case 'passe': return n ? `${n} de suite, sans que le ballon tombe.` : 'Sans que le ballon tombe.';
    default: return n ? `${n} en entier, toute l’équipe.` : 'Toute l’équipe, en entier.';
  }
}
/** « Réussi si » : celui du coach, sinon écrit tout seul (plusieurs lignes : toutes, dans l'ordre). */
export const okText = (c: Card) => (c.kind !== 'defi' ? '' : c.ok || (c.more?.length ? `${autoOk(c).replace(/\.$/, '')}, puis la suite, dans l’ordre.` : autoOk(c)));

/** Une ligne en texte : « 5 flotteurs ». */
export const lineText = (l: CardLine) => (l.n ? `${l.n} ${l.verb}` : l.verb);
/** Les lignes d'un défi, la première comprise. */
export const cardLines = (c: Card): CardLine[] => (c.kind === 'defi' ? [{ n: c.n ?? null, verb: c.verb }, ...(c.more ?? [])] : []);

/** Le titre d'une carte en une ligne : « 6 double-pas », « 5 tirs à 3 points, puis 5 flotteurs », « +2 cases », « Joker : change de défi ». */
export function cardTitle(c: Card): string {
  if (c.kind === 'action') return ACTION_LABEL[c.fx as ActionFx] ?? c.verb;
  if (c.kind === 'joker') return `Joker : ${(JOKER_LABEL[c.fx as JokerFx] ?? c.verb).toLowerCase()}`;
  return cardLines(c).map(lineText).join(', puis ');
}

// -----------------------------------------------------------------------------------------------------------------
// Un paquet pour l'un ou l'autre jeu (D-167)
// -----------------------------------------------------------------------------------------------------------------
/** Le Parcours lit une action du Sablier comme la sienne : « Sablier retourné » avance de 2, « Coup de vent » fait reculer la tête. */
export function toParcoursCard(c: Card): Card {
  if (c.kind !== 'action') return c;
  if (c.fx === 'flip') return { kind: 'action', fx: 'plus2', verb: ACTION_LABEL.plus2 };
  if (c.fx === 'wind') return { kind: 'action', fx: 'leaderBack', verb: ACTION_LABEL.leaderBack };
  return c;
}
/** Le difficile d'abord : un défi qui rapportait +2 cases, puis une relance, puis l'ordre du paquet. */
const hardness = (c: Card) => (c.win === 'plus2' ? 2 : c.win === 'replay' ? 1 : 0);
/**
 * Le paquet du Sablier, à partir de celui du Parcours (D-167) : les défis restent ; si aucun ne dit encore ce qu'il rend, les
 * 4 plus difficiles rendent +20 s ; « +2 cases » et « Rejoue » deviennent « Sablier retourné » (+30 s), « Le pion de tête
 * recule » devient « Coup de vent » (−15 s) ; les jokers restent.
 */
export function toSablierDeck(deck: Card[]): Card[] {
  const set = deck.some(c => c.kind === 'defi' && c.sec !== undefined);
  const hard = new Set(set ? [] : deck.map((c, i) => [c, i] as const).filter(([c]) => c.kind === 'defi')
    .sort((a, b) => hardness(b[0]) - hardness(a[0]) || a[1] - b[1]).slice(0, 4).map(([, i]) => i));
  return deck.map((c, i): Card => {
    if (c.kind === 'defi') return { ...c, sec: set ? c.sec ?? 0 : hard.has(i) ? 20 : 0 };
    if (c.kind === 'action') return c.fx === 'leaderBack' || c.fx === 'wind' ? { kind: 'action', fx: 'wind', verb: ACTION_LABEL.wind } : { kind: 'action', fx: 'flip', verb: ACTION_LABEL.flip };
    return c;
  });
}
/** Ce que rend le paquet du Sablier : les défis à +20 s, à +30 s, les autres, les « Sablier retourné », les « Coup de vent ». */
export function sablierMix(deck: Card[]): { gains: number; gains30: number; plain: number; flips: number; winds: number } {
  let gains = 0, gains30 = 0, flips = 0, winds = 0, plain = 0;
  for (const c of deck) {
    if (c.kind === 'defi') { if (c.sec === 30) gains30++; else if (c.sec === 20) gains++; else plain++; }
    else if (c.kind === 'action') { if (c.fx === 'wind' || c.fx === 'leaderBack') winds++; else flips++; }
  }
  return { gains, gains30, plain, flips, winds };
}

/** Combien de chaque sorte : « 6 tir · 3 dribble · 1 passe · 3 actions · 2 jokers ». */
export function deckMix(deck: Card[]): { fam: Record<Fam, number>; actions: number; jokers: number } {
  const fam = { tir: 0, dribble: 0, passe: 0, physique: 0 } as Record<Fam, number>;
  let actions = 0, jokers = 0;
  for (const c of deck) {
    if (c.kind === 'action') actions++;
    else if (c.kind === 'joker') jokers++;
    else fam[c.fam ?? 'tir']++;
  }
  return { fam, actions, jokers };
}

// -----------------------------------------------------------------------------------------------------------------
// « En faire une carte » : un exercice devient un défi (son titre et ses consignes, à ajuster)
// -----------------------------------------------------------------------------------------------------------------
const KEYWORDS: [Fam, RegExp][] = [
  ['tir', /\b(tirs?|shoot|double[- ]pas|lancers?|paniers?|finitions?|en course|cercle|3 points|mi-distance|lay)/i],
  ['dribble', /\b(dribbl\w*|changements? de main|slalom|entre les jambes|dans le dos|maniement)/i],
  ['passe', /\b(passes?|passeu\w*|contre 0|3c0|2c0|transmission)/i],
  ['physique', /\b(sprints?|gainage|pompes?|vitesse|cardio|fractionn\w*|appuis|sauts?|endurance)/i],
];
export function famOf(text: string, category?: Category | null): Fam {
  for (const [f, re] of KEYWORDS) if (re.test(text)) return f;
  return category === 'physique' ? 'physique' : 'tir';
}

/** La carte d'un exercice : la famille devinée, le nombre du titre (5 sinon), le reste en verbe, la consigne en « Réussi si ». */
export function cardFromExercise(ex: { id?: string | null; title: string; description?: string | null; category?: Category | null }): Card {
  const title = ex.title.replace(/\s+/g, ' ').trim();
  const text = `${title} ${ex.description ?? ''}`;
  const fam = famOf(text, ex.category);
  const m = /\b(\d{1,2})\b/.exec(title);
  // Un nombre dans le titre, sinon 5 pour un geste reconnu (tir, dribble…), sinon rien (« Défense sur le porteur »).
  const known = KEYWORDS.some(([, re]) => re.test(text)) || ex.category === 'physique';
  const n = m ? Math.max(1, Math.min(99, Number(m[1]))) : known ? 5 : null;
  let verb = (m ? title.replace(m[0], ' ') : title).replace(/\s+/g, ' ').trim();
  // Un titre long en deux temps (« Défense sur le porteur : fermer la ligne ») : le premier, lisible à 5 m.
  if (verb.length > 26 && /\s[:–-]\s/.test(verb)) verb = verb.split(/\s[:–-]\s/)[0]!.trim();
  verb = (verb.charAt(0).toLowerCase() + verb.slice(1)).slice(0, VERB_MAX).trim() || FAM_LABEL[fam].toLowerCase();
  const low = text.toLowerCase();
  const hand: Hand | null = /main gauche/.test(low) && /main droite/.test(low) ? null : /main gauche/.test(low) ? 'gauche' : /main droite/.test(low) ? 'droite' : null;
  const side: Side | null = /(côté|cote) gauche/.test(low) && /(côté|cote) droit/.test(low) ? 'deux' : /(côté|cote) gauche/.test(low) ? 'gauche' : /(côté|cote) droit/.test(low) ? 'droit' : null;
  let first = (ex.description ?? '').replace(/\s+/g, ' ').split(/(?<=[.!?])\s/)[0]?.trim() ?? '';
  if (first.length > OK_MAX) first = first.split(/\s[;:]\s|;\s/)[0]!.trim();
  const card: Card = { kind: 'defi', fam, n, verb, side, hand, tags: [], ok: first.length >= 8 && first.length <= OK_MAX ? first : '', win: 'plus1' };
  if (ex.id && UUID.test(ex.id)) card.exercise_id = ex.id;
  return card;
}

// -----------------------------------------------------------------------------------------------------------------
// Les 3 paquets de départ (D-162) : 15 cartes chacun — 10 défis (4 tir, 3 dribble, 2 passe, 1 physique), 3 actions
// (+2 cases, Rejoue, le pion de tête recule de 1), 2 jokers (change de défi, annule un raté). À la taille de l'âge.
// -----------------------------------------------------------------------------------------------------------------
export type StarterKey = 'u9' | 'u13' | 'u17';
export const STARTER_LABEL: Record<StarterKey, string> = { u9: 'U9–U11', u13: 'U13–U15', u17: 'U17 et adultes' };
export const STARTER_KEYS: StarterKey[] = ['u9', 'u13', 'u17'];

const d = (fam: Fam, n: number, verb: string, o: Partial<Card> = {}): Card => ({ kind: 'defi', fam, n, verb, side: null, hand: null, tags: [], ok: '', win: 'plus1', ...o });
const COMMON: Card[] = [
  { kind: 'action', fx: 'plus2', verb: ACTION_LABEL.plus2 },
  { kind: 'action', fx: 'replay', verb: ACTION_LABEL.replay },
  { kind: 'action', fx: 'leaderBack', verb: ACTION_LABEL.leaderBack },
  { kind: 'joker', fx: 'change', verb: JOKER_LABEL.change },
  { kind: 'joker', fx: 'cancel', verb: JOKER_LABEL.cancel },
];
export const STARTERS: Record<StarterKey, Card[]> = {
  u9: [
    d('tir', 5, 'paniers près du cercle', { ok: '5 paniers à un pas du panier, chacun son tour.' }),
    d('tir', 3, 'double-pas', { side: 'droit', hand: 'droite', ok: '3 paniers en double-pas, côté droit.' }),
    d('tir', 3, 'double-pas', { side: 'gauche', hand: 'gauche', ok: '3 paniers en double-pas, côté gauche.', win: 'plus2' }),
    d('tir', 2, 'lancers francs', { ok: '2 paniers, chacun tire à son tour (on peut avancer d’un pas).', win: 'replay' }),
    d('dribble', 10, 'dribbles sur place', { hand: 'gauche', ok: '10 chacun, sans perdre le ballon.' }),
    d('dribble', 2, 'allers-retours en dribble', { tags: ['sur la largeur'], ok: 'Toute l’équipe, sans perdre le ballon.' }),
    d('dribble', 5, 'changements de main', { tags: ['en marchant'], ok: '5 chacun, le ballon reste bas.' }),
    d('passe', 10, 'passes à deux mains', { tags: ['à la poitrine'], ok: '10 de suite, sans que le ballon tombe.' }),
    d('passe', 6, 'passes à terre', { tags: ['en cercle'], ok: '6 de suite, chacun reçoit au moins une fois.', win: 'replay' }),
    d('physique', 20, 'secondes de gainage', { ok: 'Toute l’équipe tient 20 secondes, sans poser les genoux.' }),
    ...COMMON,
  ],
  u13: [
    d('tir', 6, 'double-pas', { side: 'droit', hand: 'droite', ok: '6 paniers au total, on alterne.' }),
    d('tir', 4, 'tirs en course', { side: 'gauche', hand: 'gauche', ok: '3 paniers sur 4.' }),
    d('tir', 5, 'tirs à mi-distance', { tags: ['après un dribble'], ok: '5 paniers, chacun son tour.', win: 'plus2' }),
    d('tir', 6, 'lancers francs', { ok: '6 paniers, chacun tire à son tour.' }),
    d('dribble', 20, 'changements de main', { tags: ['sur place'], ok: '20 sans perdre le ballon.' }),
    d('dribble', 10, 'dribbles entre les jambes', { tags: ['en marchant'], ok: '10 chacun, sans perdre le ballon.' }),
    d('dribble', 2, 'slaloms en dribble', { hand: 'gauche', ok: 'Toute l’équipe passe les plots sans en toucher un.', win: 'plus2' }),
    d('passe', 10, 'passes à terre', { tags: ['à deux mains'], ok: '10 de suite, sans que le ballon roule.', win: 'replay' }),
    d('passe', 8, 'passes en avançant', { tags: ['sans dribble'], ok: '8 de suite jusqu’à l’autre panier, sans marcher.' }),
    d('physique', 3, 'allers-retours en sprint', { tags: ['jusqu’au milieu'], ok: 'Toute l’équipe, ligne de fond jusqu’au milieu et retour.' }),
    ...COMMON,
  ],
  u17: [
    d('tir', 5, 'tirs à 3 points', { tags: ['après une passe'], ok: '5 paniers, chacun tire à son tour.', win: 'plus2' }),
    d('tir', 6, 'double-pas', { side: 'deux', tags: ['main extérieure'], ok: '6 paniers : main gauche à gauche, main droite à droite.' }),
    d('tir', 8, 'lancers francs', { ok: '8 paniers en 10 tirs au plus, chacun à son tour.' }),
    d('tir', 5, 'tirs après un dribble', { tags: ['arrêt et tir'], ok: '5 paniers à mi-distance, après un dribble et un arrêt.' }),
    d('dribble', 30, 'changements de main', { tags: ['dos', 'entre les jambes'], ok: '30 chacun, en alternant, sans perdre le ballon.' }),
    d('dribble', 20, 'dribbles à deux ballons', { tags: ['sur place'], ok: '20 en même temps, chacun, sans perdre un ballon.', win: 'replay' }),
    d('dribble', 3, 'slaloms main faible', { ok: 'Toute l’équipe, main faible seulement, sans toucher un plot.' }),
    d('passe', 2, 'montées en 3 contre 0', { tags: ['sans dribble'], ok: '2 paniers, le ballon ne touche jamais le sol.', win: 'plus2' }),
    d('passe', 15, 'passes à une main', { tags: ['main faible'], ok: '15 de suite, sans faute.' }),
    d('physique', 10, 'pompes', { ok: '10 chacun, en entier, toute l’équipe.' }),
    ...COMMON,
  ],
};

/** Le paquet de départ à la taille de l'équipe : « U11 » → U9–U11, « U15 » → U13–U15, U17, U18, seniors → adultes. */
export function starterFor(category: string | null | undefined): StarterKey {
  const m = /U\s?(\d{1,2})/i.exec(category ?? '');
  if (m) { const age = Number(m[1]); return age <= 11 ? 'u9' : age <= 15 ? 'u13' : 'u17'; }
  return /senior|adulte|loisir|vétéran|veteran/i.test(category ?? '') ? 'u17' : 'u13';
}
export const starterDeck = (k: StarterKey): Card[] => STARTERS[k].map(c => ({ ...c, tags: [...(c.tags ?? [])] }));
